import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'

export function InboxSkeleton() {
	const isMobile = useIsMobile()

	return (
		<div className="inbox-container h-full flex overflow-hidden">
			<ConversationListSkeleton isMobile={isMobile} />
			<ChatWindowSkeleton isMobile={isMobile} />
		</div>
	)
}

function ConversationListSkeleton({ isMobile }: { isMobile: boolean }) {
	return (
		<aside
			className={cn(
				'inbox-aside w-full md:w-80 lg:w-96 flex-shrink-0 border-r'
			)}>
			<div className="flex flex-col h-full">
				{/* Header */}
				<div className="border-b px-4 py-3">
					<div className="flex items-center justify-between mb-3">
						<Skeleton className="h-6 w-24" />
						<Skeleton className="h-8 w-8 rounded-full" />
					</div>
					{/* Search */}
					<Skeleton className="h-9 w-full rounded-md" />
				</div>

				{/* Conversation items */}
				<div className="flex-1 overflow-hidden">
					<div className="space-y-1 p-2">
						{[...Array(6)].map((_, i) => (
							<ConversationItemSkeleton key={i} />
						))}
					</div>
				</div>
			</div>
		</aside>
	)
}

function ConversationItemSkeleton() {
	return (
		<div className="flex items-start gap-3 p-3 rounded-lg">
			{/* Avatar */}
			<Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
			
			<div className="flex-1 min-w-0">
				{/* Name and time */}
				<div className="flex items-start justify-between gap-2 mb-1">
					<Skeleton className="h-4 w-32" />
					<Skeleton className="h-3 w-16" />
				</div>
				
				{/* Message preview */}
				<Skeleton className="h-3 w-full mb-1" />
				<Skeleton className="h-3 w-3/4" />
				
				{/* Status badge */}
				<div className="mt-2">
					<Skeleton className="h-5 w-16 rounded-full" />
				</div>
			</div>
		</div>
	)
}

function ChatWindowSkeleton({ isMobile }: { isMobile: boolean }) {
	return (
		<main
			className={cn(
				'inbox-main flex-1 flex flex-col overflow-hidden',
				isMobile ? 'absolute inset-0 bg-background translate-x-full' : ''
			)}>
			<div className="hidden md:flex h-full flex-col">
				{/* Chat header */}
				<div className="border-b px-4 py-3">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<Skeleton className="h-10 w-10 rounded-full" />
							<div>
								<Skeleton className="h-5 w-32 mb-1" />
								<Skeleton className="h-3 w-24" />
							</div>
						</div>
						<Skeleton className="h-8 w-8 rounded-full" />
					</div>
				</div>

				{/* Messages area */}
				<div className="flex-1 overflow-hidden p-4">
					<div className="space-y-4">
						{/* User message */}
						<div className="flex justify-end">
							<div className="max-w-[70%]">
								<Skeleton className="h-4 w-48 mb-2" />
								<Skeleton className="h-16 w-64 rounded-lg" />
							</div>
						</div>
						
						{/* Agent message */}
						<div className="flex justify-start">
							<div className="max-w-[70%]">
								<div className="flex items-center gap-2 mb-2">
									<Skeleton className="h-6 w-6 rounded-full" />
									<Skeleton className="h-4 w-24" />
								</div>
								<Skeleton className="h-20 w-72 rounded-lg" />
							</div>
						</div>
						
						{/* User message */}
						<div className="flex justify-end">
							<div className="max-w-[70%]">
								<Skeleton className="h-4 w-32 mb-2" />
								<Skeleton className="h-12 w-56 rounded-lg" />
							</div>
						</div>
						
						{/* Agent message */}
						<div className="flex justify-start">
							<div className="max-w-[70%]">
								<div className="flex items-center gap-2 mb-2">
									<Skeleton className="h-6 w-6 rounded-full" />
									<Skeleton className="h-4 w-24" />
								</div>
								<Skeleton className="h-24 w-80 rounded-lg" />
							</div>
						</div>
					</div>
				</div>

				{/* Message input */}
				<div className="border-t px-4 py-3">
					<div className="flex items-end gap-2">
						<Skeleton className="h-10 flex-1 rounded-md" />
						<Skeleton className="h-10 w-10 rounded-md" />
						<Skeleton className="h-10 w-10 rounded-md" />
					</div>
				</div>
			</div>
		</main>
	)
}